import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AboutUsSiteInnerContainer from 'components/about-us/AboutUsSiteInnerContainer';
import AvatarSpeechBubble from 'components/dashboard/your-referrals/AvatarSpeechBubble';
import YourReferrals from 'components/dashboard/your-referrals/YourReferrals';
import { AvatarOnBeachTowel } from 'components/referrals/2026/AvatarOnBeachTowel';
import { AvatarOnRubberRing } from 'components/referrals/2026/AvatarOnRubberRing';
import { AvatarWithAccessory } from 'components/referrals/2026/AvatarWithAccessory';
import { ReferralInviteProgressBar } from 'components/referrals/2026/FriendsInvitedProgress';
import { WinWithFriends } from 'components/subscriptions/cancellation-flow/WinWithFriends';
import { referralsFolder } from 'constants/Referrals';
import type { PlayerInvitee } from 'hooks/referrals/usePlayerInvitesAndInvitees';
import type { TPlayer } from 'stores/UserStore';
import { DaymadeDesignSystemPreviewComponentInfo } from '..';
import { ReferralBeachHeaderFullWidth } from 'components/referrals/2026/ReferralBeachHeaderFullWidth';

const SAMPLE_AVATAR =
  'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=320&q=80';

const invitedStates = [
  { label: 'No invites yet', invitedCount: 0, totalAllowed: 3 },
  { label: '1 out of 3 invites', invitedCount: 1, totalAllowed: 3 },
  { label: 'Goal reached (3 of 3)', invitedCount: 3, totalAllowed: 3 },
  {
    label: "Broken (4 of 3, shouldn't really happen)",
    invitedCount: 4,
    totalAllowed: 3,
  },
];

const dashboardReferees: Array<PlayerInvitee> = [
  {
    avatar: SAMPLE_AVATAR,
    name: 'Sophie',
    status: 'Active',
    gracePeriodHoursRemaining: null,
  },

  {
    avatar:
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=320&q=80',
    name: 'Nina',
    status: 'Active',
    gracePeriodHoursRemaining: null,
  },
];

const dashboardCurrentUser = {
  avatarLocation: SAMPLE_AVATAR,
} as TPlayer;

const dashboardStatePreviews: Array<{
  title: string;
  description: string;
  referees: PlayerInvitee[];
  winsShared: number;
  referrerName?: string;
  referrerAvatar?: string;
}> = [
  {
    title: 'All slots filled (active + inactive)',
    description: 'Typical state with three referees and a referrer.',
    referees: dashboardReferees,
    winsShared: 45,
    referrerName: 'Priya',
    referrerAvatar: SAMPLE_AVATAR,
  },
  {
    title: 'One invite only',
    description: 'Progress has started with one active referee.',
    referees: [
      {
        avatar: SAMPLE_AVATAR,
        name: 'Dani',
        status: 'Active',
        gracePeriodHoursRemaining: null,
      },
    ],
    winsShared: 8,
    referrerName: 'Jamie',
  },
  {
    title: 'No referees yet',
    description: 'All referee slots are empty but user has a referrer.',
    referees: [],
    winsShared: 0,
    referrerName: 'Morgan',
    referrerAvatar: SAMPLE_AVATAR,
  },
  {
    title: 'No referrer',
    description: 'User has invites but was not referred by anyone.',
    referees: [
      {
        avatar: SAMPLE_AVATAR,
        name: 'Alex',
        status: 'Inactive',
        gracePeriodHoursRemaining: null,
      },
      {
        avatar: SAMPLE_AVATAR,
        name: 'Nina',
        status: 'Active',
        gracePeriodHoursRemaining: 3,
      },
    ],
    winsShared: 20,
  },
  {
    title: 'Empty state (no referees + no referrer)',
    description: 'Cold start state for a new user.',
    referees: [],
    winsShared: 0,
  },
];

const pairItems = <T,>(items: T[]): Array<T[]> => {
  const rows: Array<T[]> = [];

  for (let index = 0; index < items.length; index += 2) {
    rows.push(items.slice(index, index + 2));
  }

  return rows;
};

const ComponentPreview = () => {
  return (
    <>
      <AboutUsModule background="grey">
        <AboutUsSiteContainer>
          <h3 className="aboutUsTypographyHeaderLarge">
            Referrals 2026 Components
          </h3>
          <p>
            These demos cover core referral states: empty slots, active invites,
            inactive invitees, and completed progress milestones.
          </p>
        </AboutUsSiteContainer>

        <AboutUsSiteContainer>
          <AboutUsSiteInnerContainer
            background="white"
            borderRadius
            withPadding
          >
            <h4 className="aboutUsTypographyHeaderMedium">Avatar components</h4>
            <p>
              A component that lets you put an avatar on either a rubber ring or
              a beach towel. The width of the component will fill its parent.
              However, the rubber ring or beach towel will extend out of the
              container. This allows the parent component to have avatars that
              are the same width, regardless of if they are on a towel or a
              rubber ring. The parent component will need to provide some
              spacing on either the left or the right of the component to ensure
              the accessory does not overlap other content.
            </p>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 48,
                alignItems: 'flex-start',
              }}
            >
              <div style={{ width: 'calc(20% - (48px * 4) /5)' }}>
                <p className="aboutUsTypographyCaption">Rubber ring</p>
                <AvatarOnRubberRing avatarUrl={SAMPLE_AVATAR} />
              </div>
              <div style={{ width: 'calc(20% - (48px * 4) /5)' }}>
                <p className="aboutUsTypographyCaption">Rubber ring</p>
                <AvatarOnRubberRing avatarUrl={SAMPLE_AVATAR} color="red" />
              </div>
              <div style={{ width: 'calc(20% - (48px * 4) /5)' }}>
                <p className="aboutUsTypographyCaption">Beach towel 1</p>
                <AvatarOnBeachTowel avatarUrl={SAMPLE_AVATAR} towelIndex="1" />
              </div>
              <div style={{ width: 'calc(20% - (48px * 4) /5)' }}>
                <p className="aboutUsTypographyCaption">Beach towel 2</p>
                <AvatarOnBeachTowel
                  avatarUrl={SAMPLE_AVATAR}
                  towelIndex="2"
                  inactive
                />
              </div>
              <div style={{ width: 'calc(20% - (48px * 4) /5)' }}>
                <p className="aboutUsTypographyCaption">Beach towel 3</p>
                <AvatarOnBeachTowel avatarUrl={SAMPLE_AVATAR} towelIndex="3" />
              </div>
              <div style={{ width: 'calc(20% - (48px * 4) /5)' }}>
                <p className="aboutUsTypographyCaption">Empty slot</p>
                <AvatarWithAccessory
                  isEmptySlot
                  accessorySrc={`${referralsFolder}/beach-towel-icon-1.svg`}
                />
              </div>
            </div>
          </AboutUsSiteInnerContainer>
        </AboutUsSiteContainer>
      </AboutUsModule>

      <AboutUsModule background="white">
        <AboutUsSiteContainer>
          <h4 className="aboutUsTypographyHeaderMedium">AvatarSpeechBubble</h4>
          <p>
            An speech bubble for an avatar. It fills its container. The speech
            bubble arrow extends out of its container
          </p>
          <AvatarSpeechBubble>
            <p>This is some text within a paragraph element</p>
          </AvatarSpeechBubble>
          <p>
            It can be wrapped in an element with a max width and centering if
            necessary. It can also have a close button
          </p>
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            <AvatarSpeechBubble
              onCloseClicked={() =>
                alert(
                  'Close clicked! The owning component is responsible for deciding whether to hide it',
                )
              }
            >
              <p>This is some text within a paragraph element</p>
            </AvatarSpeechBubble>
          </div>
          <p>It provides no extra spacing around itself</p>
          <div style={{ outline: '1px solid gray', maxWidth: '250px' }}>
            <AvatarSpeechBubble>
              <p>This is some text within a paragraph element</p>
            </AvatarSpeechBubble>
          </div>
          <br />
          <div style={{ outline: '1px solid gray', maxWidth: '250px' }}>
            <AvatarSpeechBubble>
              <p>
                This is some text within a paragraph element that has
                reallylongtextwithnospaces and then some more text
              </p>
            </AvatarSpeechBubble>
          </div>
        </AboutUsSiteContainer>
      </AboutUsModule>

      <AboutUsModule background="grey">
        <AboutUsSiteContainer>
          <AboutUsSiteInnerContainer
            background="white"
            borderRadius
            withPadding
          >
            <h4 className="aboutUsTypographyHeaderMedium">
              Friends invited progress
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {invitedStates.map((state) => (
                <div key={state.label}>
                  <p className="aboutUsTypographyCaption">{state.label}</p>
                  <ReferralInviteProgressBar
                    invitedCount={state.invitedCount}
                    totalAllowed={state.totalAllowed}
                  />
                </div>
              ))}
            </div>
          </AboutUsSiteInnerContainer>
        </AboutUsSiteContainer>
      </AboutUsModule>
      <AboutUsModule background="grey">
        <AboutUsSiteContainer>
          <h4 className="aboutUsTypographyHeaderMedium">Dashboard states</h4>
          <p className="aboutUsTypographyCaption">
            Dashboard card previews for every common state, including empty
            referees and no referrer.
            <br />
            <br />
          </p>
        </AboutUsSiteContainer>

        {pairItems(dashboardStatePreviews).map((previewRow) => (
          <AboutUsSiteContainer twoColumn key={previewRow[0].title}>
            {previewRow.map((preview) => (
              <AboutUsSiteInnerContainer borderRadius key={preview.title}>
                <h4 className="aboutUsTypographyHeaderMedium">
                  {preview.title}
                </h4>
                <p className="aboutUsTypographyCaption">
                  {preview.description}
                </p>
                <YourReferrals
                  referees={preview.referees}
                  winsShared={preview.winsShared}
                  referrerName={preview.referrerName}
                  referrerAvatar={preview.referrerAvatar}
                />
              </AboutUsSiteInnerContainer>
            ))}
          </AboutUsSiteContainer>
        ))}
      </AboutUsModule>
      <AboutUsModule background="grey" withReducedPadding>
        <AboutUsSiteContainer>
          <h4 className="aboutUsTypographyHeaderMedium">
            Cancellation Flow Component
          </h4>
          <p className="aboutUsTypographyCaption">
            A preview of the component used in the cancellation flow when a
            player is playing with friends. First one for the referrer (the
            player who invited friends) and one for the invitee (the player who
            was invited by a friend).
          </p>
        </AboutUsSiteContainer>

        <AboutUsSiteContainer twoColumn>
          <AboutUsSiteInnerContainer background="white" borderRadius>
            <WinWithFriends
              isReferrer={true}
              invitees={dashboardReferees}
              currentUserAvatarUrl={
                dashboardCurrentUser.avatarLocation ?? undefined
              }
              returnToOverview={function (): void {
                alert(
                  'Return to overview clicked! The owning component is responsible for handling this action',
                );
              }}
              advanceCancellationFlow={function (): void {
                alert(
                  'Advance cancellation flow clicked! The owning component is responsible for handling this action',
                );
              }}
            />
          </AboutUsSiteInnerContainer>
          <AboutUsSiteInnerContainer background="white" borderRadius>
            <WinWithFriends
              isReferrer={false}
              invitees={[]}
              referrerAvatarUrl={SAMPLE_AVATAR}
              currentUserAvatarUrl={
                dashboardCurrentUser.avatarLocation ?? undefined
              }
              returnToOverview={function (): void {
                alert(
                  'Return to overview clicked! The owning component is responsible for handling this action',
                );
              }}
              advanceCancellationFlow={function (): void {
                alert(
                  'Advance cancellation flow clicked! The owning component is responsible for handling this action',
                );
              }}
            />
          </AboutUsSiteInnerContainer>
        </AboutUsSiteContainer>
      </AboutUsModule>
      <AboutUsModule background="white" withReducedPadding>
        <AboutUsSiteContainer>
          <h4 className="aboutUsTypographyHeaderMedium">
            Referrals page header
          </h4>
          <p className="aboutUsTypographyCaption">
            The header that appears on the referrals page. This example does not
            have someone referring the current player
          </p>
        </AboutUsSiteContainer>
      </AboutUsModule>
      <div style={{ overflow: 'hidden' }}>
        <ReferralBeachHeaderFullWidth loading={false} />
      </div>
      <AboutUsModule background="grey" withReducedPadding>
        <AboutUsSiteContainer overlapAbove twoColumn>
          <AboutUsSiteInnerContainer
            background="white"
            borderRadius
            withPadding
          >
            <p>
              This is the component that follows the beach header. It has some
              content to make it wrap to multiple lines in order to make these
              boxes bigger, so that the boxes take up enough space on the screen
              to be a realistic preview.
            </p>
          </AboutUsSiteInnerContainer>
          <AboutUsSiteInnerContainer
            background="white"
            borderRadius
            withPadding
          >
            <p>
              This is the component that follows the beach header. It has some
              content to make it wrap to multiple lines in order to make these
              boxes bigger, so that the boxes take up enough space on the screen
              to be a realistic preview.
            </p>
          </AboutUsSiteInnerContainer>
        </AboutUsSiteContainer>
      </AboutUsModule>
      <AboutUsModule background="white" withReducedPadding>
        <AboutUsSiteContainer>
          <p className="aboutUsTypographyCaption">
            The header that appears on the referrals page. This example does not
            have someone referring the current player
          </p>
        </AboutUsSiteContainer>
      </AboutUsModule>
      <div style={{ overflow: 'hidden' }}>
        <ReferralBeachHeaderFullWidth
          loading={false}
          referrerName="Christopher"
        />
      </div>
      <AboutUsModule background="grey" withReducedPadding>
        <AboutUsSiteContainer overlapAbove twoColumn>
          <AboutUsSiteInnerContainer
            background="white"
            borderRadius
            withPadding
          >
            <p>
              This is the component that follows the beach header. It has some
              content to make it wrap to multiple lines in order to make these
              boxes bigger, so that the boxes take up enough space on the screen
              to be a realistic preview.
            </p>
          </AboutUsSiteInnerContainer>
          <AboutUsSiteInnerContainer
            background="white"
            borderRadius
            withPadding
          >
            <p>
              This is the component that follows the beach header. It has some
              content to make it wrap to multiple lines in order to make these
              boxes bigger, so that the boxes take up enough space on the screen
              to be a realistic preview.
            </p>
          </AboutUsSiteInnerContainer>
        </AboutUsSiteContainer>
      </AboutUsModule>
    </>
  );
};

ComponentPreview.daymadeDesignSystemInfo = {
  name: 'Referrals 2026',
} as DaymadeDesignSystemPreviewComponentInfo;

export default ComponentPreview;
